import { useState } from "react";
import { http, apiError } from "@/lib/api";
import { X, Warning } from "@phosphor-icons/react";

/**
 * CancelSubscriptionModal — captures a structured cancellation reason + optional
 * free-text comment, then calls POST /api/payments/cancel-subscription which sets
 * cancel_at_period_end=true on Stripe and emails the coach so they can do a
 * save-attempt DM before access ends.
 *
 * Props:
 *  - open: boolean — whether the modal is visible
 *  - onClose(): close without cancelling
 *  - onCancelled(membership): called with the refreshed membership snapshot
 *    when the cancellation succeeded. The parent should refresh its membership
 *    context so the UI reflects "cancelled — access until X" state.
 */
const REASONS = [
  { key: "too_expensive", label: "Too expensive right now" },
  { key: "not_using_it", label: "Not using it enough" },
  { key: "didnt_see_results", label: "Didn't see the results I wanted" },
  { key: "life_circumstances", label: "Life got in the way (time, family, work)" },
  { key: "found_alternative", label: "Found a different program" },
  { key: "tech_issues", label: "App or technical issues" },
  { key: "other", label: "Other" },
];

export default function CancelSubscriptionModal({ open, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const reset = () => {
    setReason(""); setComment(""); setErr(""); setSubmitting(false);
  };
  const close = () => { reset(); onClose && onClose(); };

  const submit = async () => {
    if (!reason) { setErr("Please pick a reason."); return; }
    setErr(""); setSubmitting(true);
    try {
      const { data } = await http.post("/payments/cancel-subscription", {
        reason,
        comment: comment.trim() || null,
      });
      onCancelled && onCancelled(data?.membership);
      reset();
    } catch (e) {
      setErr(apiError(e, "Could not cancel subscription. Try again."));
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-4"
      data-testid="cancel-modal-backdrop"
      onClick={close}
    >
      <div
        className="w-full sm:max-w-md bg-[#0A0A0A] border border-[#FF3333] sm:border-[#262626] max-h-[92vh] overflow-y-auto"
        data-testid="cancel-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-[#262626]">
          <div className="min-w-0">
            <div className="id-label text-[#FF3333] mb-1">— Cancel subscription —</div>
            <h2 className="font-oswald text-xl uppercase tracking-wide text-white leading-tight">Before you go</h2>
          </div>
          <button
            onClick={close}
            className="text-zinc-500 hover:text-white transition-colors p-1 -mt-1 -mr-1"
            data-testid="cancel-modal-close"
            aria-label="Close"
          >
            <X size={22} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          <p className="text-sm text-zinc-300 font-manrope leading-relaxed">
            You'll keep access until the end of your current paid week, then your subscription will end. No future charges.
          </p>
          <p className="text-sm text-zinc-400 font-manrope leading-relaxed">
            Tell me what didn't work — it goes straight to Jimmy. If you've hit a wall I can help with, I'd rather know.
          </p>

          {/* Reason picker */}
          <div>
            <label className="id-label block mb-2">Why are you cancelling?</label>
            <div className="space-y-2" data-testid="cancel-reasons">
              {REASONS.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setReason(r.key)}
                  data-testid={`cancel-reason-${r.key}`}
                  className={`w-full text-left px-3 py-2.5 border text-sm font-manrope transition-colors ${
                    reason === r.key
                      ? "border-[#FF3333] bg-[#FF3333]/10 text-white"
                      : "border-[#262626] hover:border-zinc-500 text-zinc-300"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div>
            <label className="id-label block mb-2">Anything else? <span className="text-zinc-600 normal-case">(optional)</span></label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Tell Jimmy what's really going on. Total confidence — no auto-DM, just honest feedback."
              className="id-input w-full resize-none"
              data-testid="cancel-comment"
            />
            <div className="text-xs text-zinc-600 mt-1 font-manrope text-right">{comment.length}/2000</div>
          </div>

          {err && (
            <div className="flex items-start gap-2 px-3 py-2 border border-[#FF3333] bg-[#FF3333]/10 text-[#FF3333] text-sm" data-testid="cancel-error">
              <Warning size={16} weight="bold" className="flex-shrink-0 mt-0.5" />
              <div>{err}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-[#262626] flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
          <button
            onClick={close}
            disabled={submitting}
            data-testid="cancel-keep-membership"
            className="id-btn-secondary w-full sm:w-auto"
          >
            Keep my membership
          </button>
          <button
            onClick={submit}
            disabled={submitting || !reason}
            data-testid="cancel-confirm"
            className="id-btn-primary w-full sm:w-auto"
            style={{ background: "#FF3333" }}
          >
            {submitting ? "Cancelling..." : "Confirm cancellation"}
          </button>
        </div>
      </div>
    </div>
  );
}
